import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AxiosApiCall from "../api/AxiosApiCall";
import ApiServices from "../api/ApiServices";
import { toast } from "react-toastify";

const axiosClient = AxiosApiCall();

export const useCustomersList = (options = {}) => {
  return useQuery({
    queryKey: ["customersList"],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getCustomers);
      return response.data?.data || [];
    },
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useCustomerById = (customerId, options = {}) => {
  return useQuery({
    queryKey: ["customer", customerId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getCustomersDetails, {
        params: { customer_id: customerId },
      });
      return response.data?.data || null;
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useCustomerBalance = (customerId, options = {}) => {
  return useQuery({
    queryKey: ["customerBalance", customerId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getBalance, {
        params: { customer_id: customerId },
      });
      return response.data?.data || 0;
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 2,
    ...options,
  });
};

export const useCustomerWalletTransactions = (customerId, options = {}) => {
  return useQuery({
    queryKey: ["customerWalletTransactions", customerId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getWalletTransactions, {
        params: { customer_id: customerId },
      });
      return response.data?.data || [];
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 5,
    ...options,
  });
};

export const useCustomerPhysicalProducts = (customerId, options = {}) => {
  return useQuery({
    queryKey: ["customerPhysicalProducts", customerId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getPhysicalProducts, {
        params: { customer_id: customerId },
      });
      return response.data?.data || [];
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

export const useCustomerBillProducts = (customerId, options = {}) => {
  return useQuery({
    queryKey: ["customerBillProducts", customerId],
    queryFn: async () => {
      const response = await axiosClient.get(ApiServices.getOtherBills, {
        params: { customer_id: customerId },
      });
      return response.data?.data || [];
    },
    enabled: !!customerId,
    staleTime: 1000 * 60 * 10,
    ...options,
  });
};

export const useUpdateCustomerStatus = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await axiosClient.put(ApiServices.getCustomerStatus, data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["customersList"] });
      queryClient.invalidateQueries({ queryKey: ["customer"] });
      toast.success(data?.message || "Customer status updated");
      if (options.onSuccess) options.onSuccess(data);
    },
    onError: (error) => {
      const message = error?.response?.data?.message || error?.message || "Failed to update customer";
      toast.error(message);
      if (options.onError) options.onError(error);
    },
  });
};
